import React, {useEffect, useState} from 'react'
import {Button, Paper} from '@mui/material'
import Typography from '@mui/material/Typography'
import {LoaderFunctionArgs, useLoaderData, useNavigate} from 'react-router-dom'
import {useSession} from './contexts/SessionContext'
import {DataGrid, GridColDef, GridToolbarContainer, GridToolbarQuickFilter} from '@mui/x-data-grid'
import AddBoxIcon from '@mui/icons-material/AddBox'
import axios from 'axios'

export interface MagicItem {
    id: number
    item_id: string
    rep_number?: string | null
    name: string
    description?: string | null
    image_url?: string | null
    typeId?: number | null
    item_type?: {id: number; name: string} | null
    tp_value?: number | null
    note?: string | null
    duration?: number | null
    vessel_of_transference?: boolean
    eventId?: number | null
    dropped_at?: {id: number; name: string} | null
    rituals?: Array<{id: number; ritualId: number; ritual: {name: string; aspect: string; extra_text: boolean; eligible_items?: Array<{itemTypeId: number; itemType: {name: string}}>}; notes?: string | null}>
    flaws?: Array<{id: number; flawId: number; flaw: {name: string; text: string}}>
}

export const magicItemListLoader = async (_args: LoaderFunctionArgs): Promise<MagicItem[]> => {
    return axios.get('/magic-items/magic-items').then(({data}) => data)
}

const columns: GridColDef[] = [
    {field: 'item_id', headerName: 'Item ID', width: 120},
    {field: 'rep_number', headerName: 'Rep #', width: 100, valueGetter: (params: any) => params.row.rep_number ?? '—'},
    {field: 'name', headerName: 'Name', flex: 2, minWidth: 160},
    {field: 'item_type', headerName: 'Type', flex: 1, minWidth: 120,
        valueGetter: (params: any) => params.row.item_type?.name ?? '—'},
    {field: 'tp_value', headerName: 'TP', width: 80, align: 'center', headerAlign: 'center',
        valueGetter: (params: any) => params.row.tp_value ?? '—'},
    {field: 'duration', headerName: 'Duration', width: 90, align: 'center', headerAlign: 'center',
        valueGetter: (params: any) => params.row.duration ?? '—'},
    {field: 'dropped_at', headerName: 'Dropped At', flex: 1, minWidth: 120,
        valueGetter: (params: any) => params.row.dropped_at?.name ?? '—'},
]

const CMA_ROLES = ['GM', 'Owner']

function MagicItemListToolbar({onNew, chapterNumber, showCma}: {onNew: () => void, chapterNumber: number | null, showCma: boolean}) {
    const cmaBase = showCma && chapterNumber != null
        ? `https://cma.alliancelarp.com/chapters/${chapterNumber}/items`
        : null

    return (
        <GridToolbarContainer sx={{justifyContent: 'space-between', p: 1}}>
            <div style={{display: 'flex', gap: 8}}>
                <Button startIcon={<AddBoxIcon/>} variant="contained" size="small" onClick={onNew}>
                    New Magic Item
                </Button>
                {cmaBase && (
                    <>
                        <Button variant="outlined" size="small" href={cmaBase} target="_blank" rel="noopener noreferrer">
                            CMA Items
                        </Button>
                        <Button variant="outlined" size="small" href={`${cmaBase}/new`} target="_blank" rel="noopener noreferrer">
                            New CMA Item
                        </Button>
                    </>
                )}
            </div>
            <GridToolbarQuickFilter/>
        </GridToolbarContainer>
    )
}

export function MagicItemList() {
    const [items] = useState(useLoaderData() as MagicItem[])
    const [chapterNumber, setChapterNumber] = useState<number | null>(null)
    const {roles} = useSession()
    const showCma = CMA_ROLES.some(r => roles.includes(r))
    const navigate = useNavigate()

    useEffect(() => {
        axios.get('/admin/chapter').then(({data}) => {
            if (data?.chapterId) setChapterNumber(data.chapterId)
        }).catch(() => {})
    }, [])

    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Typography variant="h5" gutterBottom>Magic Item Manager</Typography>
            <DataGrid
                rows={items}
                columns={columns}
                autoHeight
                pageSizeOptions={[25, 50, 100]}
                initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                slots={{toolbar: () => <MagicItemListToolbar onNew={() => navigate('new')} chapterNumber={chapterNumber} showCma={showCma}/>}}
                onRowClick={(row) => navigate(String(row.id))}
                sx={{cursor: 'pointer'}}
            />
        </Paper>
    )
}
